import { css } from '@emotion/react';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Flex } from '@inflearn/ds-react-next';
import { useLang } from '@inflearn/i18n-tools';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import type { SignupPhoneVerificationRequest } from '../../../../resources/@types/accountSchema';
import type { Captcha } from '../../../hooks/useCaptcha';
import useClientAuthPhoneVerificationCheckCreateMutation from '../../../mutations/useClientAuthPhoneVerificationCheckCreateMutation';
import { useClientAuthChangePhoneNumberCreateMutation } from '../../../mutations/useClientAuthPhoneVerificationCheckCreateVariables';
import { PhoneNumberValidation } from '../../../validators/PhoneNumberValidation';
import { PhoneVerificationForm } from '../../common/PhoneVerificationForm';
import VerificationCodeInput, {
  type VerificationCodeInputProps,
} from '../../common/VerificationCodeInput';

type PhoneVerificationSectionProps = {
  isVerified: boolean;
  /**
   * 인증을 마친 번호와 인증번호. 비로그인 가입은 이 값을 가입 요청(phone)에 그대로 실어야 계정에 번호가 저장된다.
   * isSignUpDiscountDeleted는 로그인 사용자의 번호 저장(/change/phone-number) 결과이고, 비로그인이면 false다.
   */
  onVerified: (
    phone: SignupPhoneVerificationRequest,
    result: { isSignUpDiscountDeleted: boolean },
  ) => void;
} & (
  | {
      /**
       * 비로그인: /phone-verification/public(캡차)으로 발송하고 /phone-verification/check로 검증한다.
       * 번호는 가입 요청(v5 이메일 / v3 소셜)에서 저장된다.
       */
      userType: 'guest';
      captcha: Captcha;
    }
  | {
      /**
       * 로그인: /phone-verification으로 발송하고 /change/phone-number로 검증과 함께 계정 번호를 저장한다.
       */
      userType: 'member';
    }
);

export function PhoneVerificationSection(props: PhoneVerificationSectionProps) {
  const { isVerified, onVerified, userType } = props;
  const isGuest = userType === 'guest';
  const { countryCode } = useLang();
  const [isSend, setIsSend] = useState(false);

  const form = useForm<PhoneNumberValidation>({
    mode: 'all',
    resolver: classValidatorResolver(PhoneNumberValidation),
    defaultValues: {
      countryCode,
    },
  });

  const phoneVerificationCheckMutation = useClientAuthPhoneVerificationCheckCreateMutation();
  const changePhoneNumberMutation = useClientAuthChangePhoneNumberCreateMutation();

  const handleVerify: VerificationCodeInputProps['onClickCompletes'] = (data, options) => {
    const verifiedPhone = {
      countryCode: data.countryCode,
      phoneNumber: data.phone,
      authCode: data.verifyCode,
    };

    if (isGuest) {
      phoneVerificationCheckMutation.mutate(
        { phone: data.phone, countryCode: data.countryCode, verifyCode: data.verifyCode },
        {
          onSuccess: () => onVerified(verifiedPhone, { isSignUpDiscountDeleted: false }),
          onError: options?.onError,
        },
      );
      return;
    }

    changePhoneNumberMutation.mutate(
      { phoneNumber: data.phone, countryCode: data.countryCode, authCode: data.verifyCode },
      {
        onSuccess: (res) =>
          onVerified(verifiedPhone, {
            isSignUpDiscountDeleted: Boolean(res.data?.isSignUpDiscountDeleted),
          }),
        onError: options?.onError,
      },
    );
  };

  return (
    <FormProvider {...form}>
      <Flex direction="column" w="100%">
        <PhoneVerificationForm
          showPhoneInputLabel
          onSend={() => setIsSend(true)}
          isSend={isSend}
          withCaptcha={isGuest}
          sharedCaptcha={isGuest ? props.captcha : undefined}
          isVerified={isVerified}
          fullWidthSendButton
        />
        {isSend && !isVerified && (
          <VerificationCodeInput
            onClickCompletes={handleVerify}
            styleForm={css({ marginTop: 24, flexWrap: 'wrap', button: { width: '100%' } })}
          />
        )}
      </Flex>
    </FormProvider>
  );
}
